import React, { useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ButtonGroup,
  Typography,
  TextField,
  Paper,
  LinearProgress,
  IconButton,
} from '@material-ui/core';
import { Send, PhotoCamera } from '@material-ui/icons';
import * as Sentry from '@sentry/browser';
import useWindowDimensions from '../hooks/useWindowDimensions';
import { setChatDialog, chatRead, chatViewing } from './redux';
import {
  getMySchedules,
  getTaskChats,
  createTaskChat,
  getScheduleChats,
  createScheduleChat,
  getMessages,
  getReceipts,
  sendMessage,
  uploadAttachment,
  rememberAttachmentUrl,
  closeChat,
  getCategories,
  getQuickReplies,
  selectCategory,
  escalateChat,
  newClientMessageId,
} from './chatApi';
import { startChatConnection, onChatConnected, joinChat, leaveChat } from './chatHub';
import {
  reportDelivered,
  reportRead,
  usePageVisible,
  upsertReceipt,
  messageStatus,
} from './receipts';
import MessageStatus from './MessageStatus';
import { withChatBoundary } from './ChatErrorBoundary';
import { senderType, chatColors, isUnread } from './chatState';
import { compressImage, MessageAttachments, DraftAttachments } from './ChatAttachments';

const chatStatus = { selfService: 1, waitingCoordinator: 2, claimed: 3, closed: 4 };
// Координаторът смени темата: чатът е приключен и разговорът продължава в нов чат с новата тема.
const closeReason = { topicChanged: 5 };
// Цветът е по роля (еднакъв в таблета и в графиците), страната — дали съобщението е мое в тази роля.
const bubbleColor = { [senderType.technician]: '#a5daff', [senderType.coordinator]: '#ffd8a8' };
const roleLabel = { [senderType.technician]: 'техник', [senderType.coordinator]: 'координатор' };
const nodeType = { group: 1 };

// Крайните теми с пътя до тях („Група › Подтема › Тема"), в реда на дървото — за търсенето над темите.
const finalTopics = categories => {
  const walk = (parentId, prefix) =>
    categories
      .filter(x => (x.parentId || null) === parentId)
      .flatMap(x => {
        const path = prefix ? `${prefix} › ${x.title}` : x.title;
        return x.nodeType === nodeType.group ? walk(x.id, path) : [{ id: x.id, path }];
      });
  return walk(null, '');
};
const systemEvent = { autoAnswer: 7, descriptionRequested: 8 };

// Линковете в автоматичните отговори (устройствата и DTV мониторингът в OSS) — отварят се като другите линкове към bbs2
// в таблета (window.open, нов прозорец). Ключът е мястото в текста.
const urlPattern = /(https?:\/\/\S+)/;
const linkify = text => {
  let offset = 0;
  return (text || '').split(urlPattern).map(part => {
    const key = offset;
    offset += part.length;
    if (!urlPattern.test(part)) return part;
    return (
      <a
        key={key}
        href={part}
        style={{ wordBreak: 'break-all' }}
        onClick={e => {
          e.preventDefault();
          window.open(part, '_blank');
        }}
      >
        {part}
      </a>
    );
  });
};

const formatTime = value =>
  value ? new Date(value).toLocaleTimeString('bg-BG', { hour: '2-digit', minute: '2-digit' }) : '';

// Час на стартиране на чата — с датата, ако не е днес.
const formatStart = value => {
  const date = new Date(value);
  if (date.toDateString() === new Date().toDateString()) return formatTime(value);
  const day = date.toLocaleDateString('bg-BG', { day: '2-digit', month: '2-digit' });
  return `${day} ${formatTime(value)}`;
};

const statusText = chat => {
  switch (chat.status) {
    case chatStatus.selfService:
      return chat.scheduleTaskId ? 'Изберете тема' : 'Напишете съобщение';
    case chatStatus.waitingCoordinator:
      return 'Изчаква се координатор';
    case chatStatus.claimed:
      return `Поет от ${chat.claimedByName || 'координатор'}`;
    case chatStatus.closed:
      return `Приключен ${formatTime(chat.closedOn)}`;
    default:
      return '';
  }
};

// Съобщение с id от сървъра заменя чакащото/неуспешното със същото clientMessageId — така не се дублира,
// когато MessageAdded от хъба пристигне преди отговора на POST.
const mergeMessages = (current, incoming) => {
  const byClientId = new Map(current.map(x => [x.clientMessageId, x]));
  incoming.forEach(x => {
    const existing = byClientId.get(x.clientMessageId);
    if (!existing || !existing.id || x.id) byClientId.set(x.clientMessageId, x);
  });
  return [...byClientId.values()].sort(
    (a, b) => (a.id || Number.MAX_SAFE_INTEGER) - (b.id || Number.MAX_SAFE_INTEGER)
  );
};

const lastServerId = messages =>
  messages.reduce((max, x) => (x.id && x.id > max ? x.id : max), 0) || undefined;

const belongsToTarget = (chat, target) =>
  target &&
  (target.scheduleTaskId
    ? chat.scheduleTaskId === target.scheduleTaskId
    : !chat.scheduleTaskId && chat.scheduleByDateId === target.scheduleByDateId);

const upsertChat = (chats, chat) =>
  chats.some(x => x.id === chat.id)
    ? chats.map(x => (x.id === chat.id ? chat : x))
    : [...chats, chat].sort((a, b) => a.id - b.id);

const errorText = error =>
  error && error.message && error.message.includes('fetch')
    ? 'Няма връзка със сървъра на чата.'
    : (error && error.message) || 'Възникна грешка в чата.';

const ChatDialog = () => {
  const dispatch = useDispatch();
  const { fontSize } = useWindowDimensions();
  const chatDialog = useSelector(state => state.chat.chatDialog);
  const user = useSelector(state => state.auth.user);
  const attachmentsConfig = useSelector(state => state.chat.attachments);
  // Чатовете с невидяно входящо — и приключени (координаторът отговори и приключи): те се отварят първи.
  const unreadChatIds = useSelector(
    state => state.chat.activeChats.filter(x => isUnread(state.chat, x)).map(x => x.id),
    shallowEqual
  );

  const [target, setTarget] = useState(null);
  const [chats, setChats] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [text, setText] = useState('');
  const [drafts, setDrafts] = useState([]);
  const visible = usePageVisible();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Прозорецът не е заредил (чатовете, съобщенията или темите) — „Опитай пак" го зарежда отначало.
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);
  const [categories, setCategories] = useState([]);
  // Готовите текстове на техника в общия чат (като бързите отговори на координатора в графиците) — с едно натискане,
  // като обикновено съобщение; свободният текст остава.
  const [quickReplies, setQuickReplies] = useState([]);
  // undefined = следваме избраната тема на чата; null = всички теми от първо ниво.
  const [browseParentId, setBrowseParentId] = useState(undefined);
  const [topicSearch, setTopicSearch] = useState('');
  const [stepping, setStepping] = useState(false);
  // Тема с потвърждение (рестарт на устройство), чакаща „Да" от техника.
  const [confirmTopic, setConfirmTopic] = useState(null);

  const targetRef = useRef(target);
  const unreadChatIdsRef = useRef(unreadChatIds);
  const replacedChatIdRef = useRef(null);
  const selectedChatIdRef = useRef(selectedChatId);
  const messagesRef = useRef(messages);
  const endRef = useRef(null);
  const fileInputRef = useRef(null);
  targetRef.current = target;
  unreadChatIdsRef.current = unreadChatIds;
  selectedChatIdRef.current = selectedChatId;
  messagesRef.current = messages;

  const activeChat = chats.find(x => x.status !== chatStatus.closed);
  const selectedChat = chats.find(x => x.id === selectedChatId);
  const canWrite = selectedChat && selectedChat.status !== chatStatus.closed;
  const historyChats = chats.filter(x => x.status === chatStatus.closed);

  const isSelfService = !!selectedChat && selectedChat.status === chatStatus.selfService;
  // Тема, която остава отворена след приключване на задачата — чатът се приключва от координатора (28.10.2026); докато
  // е в самообслужване („Помогна ли?" след готов отговор), техникът го приключва, както досега.
  const canClose = canWrite && (isSelfService || !selectedChat.keepOpenOnTaskClose);
  const lastMessage = messages[messages.length - 1];
  const awaitingFeedback =
    isSelfService && !!lastMessage && lastMessage.systemEvent === systemEvent.autoAnswer;
  // Общият чат: техникът избира тема или пише направо на координатора — първото съобщение вкарва чата в опашката.
  const isGeneralChat = !!selectedChat && !selectedChat.scheduleTaskId;
  // Темата изисква координатор: техникът първо описва проблема — с това съобщение чатът влиза в опашката. Стигнал до
  // крайна тема, техникът не я сменя — сменя я координаторът.
  const describeMode =
    isSelfService &&
    (isGeneralChat ||
      (!!lastMessage && lastMessage.systemEvent === systemEvent.descriptionRequested));
  // Няма активен чат и не се гледа стар: по задача — направо темите, общ — направо полето за писане. Чатът се създава
  // с избора на крайна тема / първото съобщение (openNewChat).
  const newChat = !loading && !!target && !activeChat && !selectedChat;
  const generalTarget = !!target && !target.scheduleTaskId;
  const showInput = (canWrite && !isSelfService) || describeMode || (newChat && generalTarget);
  const currentCategory = selectedChat && categories.find(x => x.id === selectedChat.categoryId);
  const currentGroupId =
    currentCategory && currentCategory.nodeType === nodeType.group
      ? currentCategory.id
      : (currentCategory && currentCategory.parentId) || null;
  const parentId = browseParentId !== undefined ? browseParentId : currentGroupId;
  // Темите на общия чат (27.10.2026) — над полето за писане, докато не е избрана крайна тема; полето остава.
  const hasFinalTopic = !!currentCategory && currentCategory.nodeType !== nodeType.group;
  const generalTopics =
    categories.length > 0 &&
    ((newChat && generalTarget) ||
      (isGeneralChat && isSelfService && !awaitingFeedback && !hasFinalTopic));
  const topicOptions = categories.filter(x => (x.parentId || null) === (parentId || null));
  const browsedGroup = parentId ? categories.find(x => x.id === parentId) : null;
  // Търсене над темите (функционалното описание, т. 3.1.2): всяка написана дума трябва да я има в пътя на крайната
  // тема; докато полето е празно — изборът е по нива.
  const searchWords = topicSearch
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  const searchResults = searchWords.length
    ? finalTopics(categories).filter(x =>
        searchWords.every(word => x.path.toLowerCase().includes(word))
      )
    : null;
  // Снимки — само по теми, които ги позволяват (отметката важи и за подтемите — сървисът я смята), или в чат без тема
  // (започнат от координатор).
  const photosAllowed =
    !!attachmentsConfig &&
    (selectedChat
      ? !selectedChat.categoryId || !!(currentCategory && currentCategory.allowsAttachments)
      : newChat && generalTarget);

  const reportError = e => {
    setError(errorText(e));
    if (!e.status || e.status >= 500) Sentry.captureException(e);
  };
  const reportLoadError = e => {
    reportError(e);
    setLoadFailed(true);
  };

  // Какво е отворено: чатовете на задачата или общият чат на днешния график.
  useEffect(() => {
    if (!chatDialog.open) return undefined;
    let cancelled = false;

    setTarget(null);
    setChats([]);
    setSelectedChatId(null);
    setMessages([]);
    setBrowseParentId(undefined);
    setTopicSearch('');
    setCategories([]);
    setError(null);
    setLoadFailed(false);
    setLoading(true);

    // Общият чат — своите теми (без автоматични отговори) и готовите текстове; без тях остава полето за писане.
    if (chatDialog.scheduleTaskId) {
      getCategories(true)
        .then(list => !cancelled && setCategories(list))
        .catch(e => !cancelled && reportLoadError(e));
    } else {
      getCategories(false, true)
        .then(list => !cancelled && setCategories(list || []))
        .catch(() => !cancelled && setCategories([]));
      getQuickReplies()
        .then(list => !cancelled && setQuickReplies(list || []))
        .catch(() => !cancelled && setQuickReplies([]));
    }

    (async () => {
      try {
        let newTarget;
        if (chatDialog.scheduleTaskId) {
          newTarget = { scheduleTaskId: chatDialog.scheduleTaskId, title: chatDialog.title };
        } else {
          const schedules = await getMySchedules();
          if (!schedules.length) throw new Error('Нямате график за днес.');
          newTarget = {
            scheduleByDateId: schedules[0].scheduleByDateId,
            title: `Общ чат на графика ${schedules[0].scheduleName}`,
          };
        }

        const list = newTarget.scheduleTaskId
          ? await getTaskChats(newTarget.scheduleTaskId)
          : await getScheduleChats(newTarget.scheduleByDateId);

        if (cancelled) return;
        setTarget(newTarget);
        setChats(list);
        const unread = list.find(
          x => x.status === chatStatus.closed && unreadChatIdsRef.current.includes(x.id)
        );
        const shown = unread || list.find(x => x.status !== chatStatus.closed);
        setSelectedChatId(shown ? shown.id : null);
      } catch (e) {
        if (!cancelled) reportLoadError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [chatDialog.open, chatDialog.scheduleTaskId, chatDialog.title, reloadCount]);

  // Събитията от хъба: нови съобщения в отворения чат и промени по чатовете на графика.
  useEffect(() => {
    if (!chatDialog.open) return undefined;
    let connection = null;

    const onMessageAdded = message => {
      if (message.chatId !== selectedChatIdRef.current) return;
      setMessages(current => mergeMessages(current, [message]));
      reportDelivered(message.chatId, message.id);
    };
    const onChatChanged = chat => {
      if (!belongsToTarget(chat, targetRef.current)) return;
      setChats(current => upsertChat(current, chat));
      if (chat.id === selectedChatIdRef.current && chat.closeReason === closeReason.topicChanged)
        replacedChatIdRef.current = chat.id;
    };
    const onReceiptUpdated = receipt => {
      if (receipt.chatId === selectedChatIdRef.current)
        setReceipts(current => upsertReceipt(current, receipt));
    };

    startChatConnection()
      .then(c => {
        connection = c;
        c.on('MessageAdded', onMessageAdded);
        c.on('ChatCreated', onChatChanged);
        c.on('ChatUpdated', onChatChanged);
        c.on('ReceiptUpdated', onReceiptUpdated);
      })
      .catch(() => true);

    return () => {
      if (connection) {
        connection.off('MessageAdded', onMessageAdded);
        connection.off('ChatCreated', onChatChanged);
        connection.off('ChatUpdated', onChatChanged);
        connection.off('ReceiptUpdated', onReceiptUpdated);
      }
    };
  }, [chatDialog.open]);

  // Колега от екипа е започнал чат, докато този е отворен — показваме го. Координаторът смени темата на гледания
  // чат — показваме новия, в който продължава разговорът (събитията за стария и новия идват в произволен ред).
  useEffect(() => {
    if (!activeChat) return;
    const replaced = replacedChatIdRef.current === selectedChatId && activeChat.id > selectedChatId;
    if (selectedChatId && !replaced) return;
    replacedChatIdRef.current = null;
    setSelectedChatId(activeChat.id);
  }, [selectedChatId, activeChat]);

  // Отвореният чат: влизаме в групата му и дърпаме съобщенията; при повторно свързване — само пропуснатото.
  useEffect(() => {
    if (!chatDialog.open || !selectedChatId) return undefined;
    const chatId = selectedChatId;
    let cancelled = false;

    // Групата на чата се пази от chatHub и при повторно свързване; тук — само пропуснатото и отметките.
    const sync = async () => {
      const [list, currentReceipts] = await Promise.all([
        getMessages(chatId, lastServerId(messagesRef.current)),
        getReceipts(chatId).catch(() => []),
      ]);
      if (cancelled) return;
      setMessages(current => mergeMessages(current, list));
      setReceipts(currentReceipts || []);
    };

    joinChat(chatId);
    setMessages([]);
    setReceipts([]);
    messagesRef.current = [];
    setBrowseParentId(undefined);
    setTopicSearch('');
    setDrafts([]);
    let unsubscribe = () => true;
    sync()
      .catch(e => !cancelled && reportLoadError(e))
      .finally(() => {
        if (!cancelled) unsubscribe = onChatConnected(() => sync().catch(() => true));
      });

    return () => {
      cancelled = true;
      unsubscribe();
      leaveChat(chatId);
    };
  }, [chatDialog.open, selectedChatId]);

  // Видяно: прозорецът с чата е отворен, а таблетът — на преден план. Индикаторът става зелен веднага, без да чака
  // сървъра.
  const lastMessageId = lastServerId(messages);
  useEffect(() => {
    if (chatDialog.open && selectedChatId && visible && lastMessageId) {
      reportRead(selectedChatId, lastMessageId);
      dispatch(chatRead(selectedChatId, lastMessageId));
    }
  }, [chatDialog.open, selectedChatId, visible, lastMessageId]);

  // За гледания чат известие за ново съобщение не излиза (ChatNotices).
  const viewingChatId = chatDialog.open && visible ? selectedChatId : null;
  useEffect(() => {
    dispatch(chatViewing(viewingChatId));
  }, [viewingChatId]);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ block: 'end' });
  }, [messages]);

  // Чатът се създава с първото действие на техника (newChat); ако колега от екипа го е започнал междувременно —
  // продължава в неговия.
  const openNewChat = async () => {
    let chat;
    try {
      chat = target.scheduleTaskId
        ? await createTaskChat(target.scheduleTaskId)
        : await createScheduleChat(target.scheduleByDateId);
    } catch (e) {
      if (e.status !== 409 || !e.existing) throw e;
      chat = e.existing;
    }
    setChats(current => upsertChat(current, chat));
    setSelectedChatId(chat.id);
    return chat;
  };

  // Отговорът на стъпка (тема, изпратено съобщение) носи и новите съобщения — не чакаме хъба, който може да е без връзка.
  const applyStep = step => {
    setChats(current => upsertChat(current, step.chat));
    setMessages(current => mergeMessages(current, step.messages));
  };

  // Снимките се качват преди съобщението; качените пазят id-то си, за да не се качват пак при „опитай пак".
  const send = async (clientMessageId, body, uploads = []) => {
    let chatId = selectedChatId;
    if (!chatId) {
      try {
        chatId = (await openNewChat()).id;
      } catch (e) {
        setText(body);
        setDrafts(uploads);
        reportError(e);
        return;
      }
    }
    setMessages(current =>
      mergeMessages(current, [
        {
          clientMessageId,
          chatId,
          body,
          uploads,
          senderUserId: user && user.id,
          senderType: senderType.technician,
          pending: true,
        },
      ])
    );

    let uploaded = uploads;
    try {
      const results = await Promise.allSettled(
        uploads.map(x =>
          x.attachmentId
            ? x.attachmentId
            : uploadAttachment(chatId, x.blob).then(attachment => {
                rememberAttachmentUrl(attachment.id, x.previewUrl);
                return attachment.id;
              })
        )
      );
      uploaded = uploads.map((x, i) =>
        results[i].status === 'fulfilled' ? { ...x, attachmentId: results[i].value } : x
      );
      const failure = results.find(x => x.status === 'rejected');
      if (failure) throw failure.reason;

      // Отговорът носи и „Изчаква се координатор…", когато това е описанието, с което чатът влиза в опашката.
      applyStep(
        await sendMessage(
          chatId,
          clientMessageId,
          body,
          uploaded.map(x => x.attachmentId)
        )
      );
    } catch (e) {
      setMessages(current =>
        current.map(x =>
          x.clientMessageId === clientMessageId
            ? { ...x, uploads: uploaded, pending: false, failed: true }
            : x
        )
      );
      if (e.status === 409 && e.existing) setChats(current => upsertChat(current, e.existing));
      reportError(e);
    }
  };

  const runStep = async action => {
    setStepping(true);
    setError(null);
    try {
      applyStep(await action());
      setBrowseParentId(undefined);
      setTopicSearch('');
    } catch (e) {
      if (e.status === 409 && e.existing) setChats(current => upsertChat(current, e.existing));
      reportError(e);
    } finally {
      setStepping(false);
    }
  };

  // Групите се разлистват само тук — в чата отива само крайната тема (потребителят, 23.10.2026), а не всяка стъпка
  // до нея. Затова и чатът се създава чак с крайната тема.
  const runTopic = (category, confirmed) =>
    runStep(async () => {
      const chatId = selectedChatId || (await openNewChat()).id;
      return selectCategory(chatId, newClientMessageId(), category.id, confirmed);
    });

  const onSelectCategory = picked => {
    // Резултатите от търсенето носят само id и пътя.
    const category = categories.find(x => x.id === picked.id) || picked;
    if (category.nodeType === nodeType.group) {
      setBrowseParentId(category.id);
      setTopicSearch('');
      return;
    }
    // Рестартът прекъсва услугата на клиента — само след „Да" (сървърът отказва без потвърждение).
    if (category.requiresConfirmation) {
      setConfirmTopic(category);
      return;
    }
    runTopic(category, false);
  };

  const onEscalate = () => runStep(() => escalateChat(selectedChatId));

  const onResolved = async () => {
    setStepping(true);
    setError(null);
    try {
      const chat = await closeChat(selectedChatId);
      setChats(current => upsertChat(current, chat));
    } catch (e) {
      if (e.status === 409 && e.existing) setChats(current => upsertChat(current, e.existing));
      else reportError(e);
    } finally {
      setStepping(false);
    }
  };

  const onSend = () => {
    const body = text.trim();
    if ((!body && !drafts.length) || !showInput) return;
    setText('');
    setDrafts([]);
    send(newClientMessageId(), body, drafts);
  };

  const onPickPhotos = async e => {
    const files = [...e.target.files];
    e.target.value = '';
    const room = attachmentsConfig.maxPerMessage - drafts.length;
    if (files.length > room)
      setError(`До ${attachmentsConfig.maxPerMessage} снимки в едно съобщение.`);
    try {
      const added = await Promise.all(
        files.slice(0, Math.max(room, 0)).map(async file => {
          const blob = await compressImage(file);
          return { blob, previewUrl: URL.createObjectURL(blob) };
        })
      );
      setDrafts(current => [...current, ...added]);
    } catch (err) {
      setError(err.message);
    }
  };

  const onCloseChat = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Да приключа ли чата?')) return;
    setError(null);
    try {
      const chat = await closeChat(selectedChatId);
      setChats(current => upsertChat(current, chat));
    } catch (e) {
      if (e.status === 409 && e.existing) setChats(current => upsertChat(current, e.existing));
      else reportError(e);
    }
  };

  const closeDialog = () =>
    dispatch(setChatDialog({ open: false, scheduleTaskId: null, title: '' }));

  const renderMessage = message => {
    if (message.senderType === senderType.system) {
      // Автоматичният отговор (напр. „История на предишни посещения") е на няколко реда — вляво, както е написан, с
      // часа долу вдясно, както при съобщенията.
      if (message.systemEvent === systemEvent.autoAnswer) {
        return (
          <div
            key={message.clientMessageId}
            style={{
              backgroundColor: '#f1f1f1',
              borderRadius: '6px',
              padding: '6px',
              margin: '6px 0',
            }}
          >
            <Typography style={{ ...fontSize, whiteSpace: 'pre-wrap' }}>
              {linkify(message.body)}
            </Typography>
            <Typography style={{ fontSize: '0.75em', color: '#555', textAlign: 'right' }}>
              {formatTime(message.createdOn)}
            </Typography>
          </div>
        );
      }
      return (
        <Typography
          key={message.clientMessageId}
          style={{ textAlign: 'center', color: '#777', fontStyle: 'italic', margin: '6px 0' }}
        >
          {message.body} · {formatTime(message.createdOn)}
        </Typography>
      );
    }

    // Един потребител може да е и координатор — неговият отговор от графиците не е „мое" съобщение тук.
    const own =
      message.senderType === senderType.technician && !!user && message.senderUserId === user.id;
    return (
      <div
        key={message.clientMessageId}
        style={{
          display: 'flex',
          justifyContent: own ? 'flex-end' : 'flex-start',
          margin: '4px 0',
        }}
      >
        <Paper
          style={{
            padding: '6px 10px',
            maxWidth: '80%',
            backgroundColor: bubbleColor[message.senderType] || '#eeeeee',
          }}
        >
          {!own && (
            <Typography style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
              {[message.senderName, roleLabel[message.senderType]].filter(Boolean).join(' · ')}
            </Typography>
          )}
          <MessageAttachments
            attachments={message.attachments}
            previews={message.uploads && message.uploads.map(x => x.previewUrl)}
          />
          {message.body && (
            <Typography style={{ ...fontSize, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {message.body}
            </Typography>
          )}
          <Typography style={{ fontSize: '0.75em', color: '#555', textAlign: 'right' }}>
            {message.pending && 'Изпраща се…'}
            {message.failed && (
              <Button
                size="small"
                color="secondary"
                onClick={() => send(message.clientMessageId, message.body, message.uploads)}
              >
                Неуспешно — опитай пак
              </Button>
            )}
            {!message.pending && !message.failed && formatTime(message.createdOn)}
            {own && !message.pending && !message.failed && (
              <MessageStatus status={messageStatus(message, receipts, senderType.coordinator)} />
            )}
          </Typography>
        </Paper>
      </div>
    );
  };

  return (
    chatDialog.open && (
      <Dialog open={chatDialog.open} fullScreen>
        <ButtonGroup style={{ position: 'sticky', width: '100%', top: '0', zIndex: '100' }}>
          <Button
            onClick={closeDialog}
            variant="contained"
            style={{ width: canClose ? '50%' : '100%' }}
          >
            <Typography gutterBottom component="span" style={fontSize}>
              Назад
            </Typography>
          </Button>
          {canClose && (
            <Button
              onClick={onCloseChat}
              variant="contained"
              color="secondary"
              style={{ width: '50%' }}
            >
              <Typography gutterBottom component="span" style={fontSize}>
                Приключи чата
              </Typography>
            </Button>
          )}
        </ButtonGroup>

        <Paper style={{ padding: '6px', margin: '3px' }}>
          <Typography style={{ ...fontSize, fontWeight: 'bold' }}>
            {(target && target.title) || chatDialog.title || 'Чат с координатор'}
          </Typography>
          {selectedChat && (
            <Typography>{`Започнат: ${formatStart(selectedChat.createdOn)}`}</Typography>
          )}
          {selectedChat && selectedChat.categoryPath && (
            <Typography>{`Тема: ${selectedChat.categoryPath}`}</Typography>
          )}
          {selectedChat && <Typography>{statusText(selectedChat)}</Typography>}
          {canWrite && !canClose && <Typography>Чатът се приключва от координатора.</Typography>}
        </Paper>

        {loading && <LinearProgress />}
        {error && (
          <Typography color="error" style={{ margin: '3px 6px' }}>
            {error}
          </Typography>
        )}
        {loadFailed && !loading && (
          <Button
            variant="contained"
            color="primary"
            style={{ margin: '3px 6px' }}
            onClick={() => setReloadCount(x => x + 1)}
          >
            <Typography component="span" style={fontSize}>
              Опитай пак
            </Typography>
          </Button>
        )}

        {historyChats.length > 0 && (
          <div style={{ margin: '3px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {historyChats.map(x => (
              <Button
                key={x.id}
                size="small"
                variant={x.id === selectedChatId ? 'contained' : 'outlined'}
                onClick={() => setSelectedChatId(x.id)}
                style={
                  x.id !== selectedChatId && unreadChatIds.includes(x.id)
                    ? { color: chatColors.unread, borderColor: chatColors.unread }
                    : undefined
                }
              >
                Чат от {formatTime(x.createdOn)}
              </Button>
            ))}
            {activeChat ? (
              <Button
                size="small"
                color="secondary"
                variant={activeChat.id === selectedChatId ? 'contained' : 'outlined'}
                onClick={() => setSelectedChatId(activeChat.id)}
              >
                Активен чат
              </Button>
            ) : (
              <Button
                size="small"
                color="secondary"
                variant={selectedChatId ? 'outlined' : 'contained'}
                onClick={() => {
                  setSelectedChatId(null);
                  setMessages([]);
                }}
              >
                Нов чат
              </Button>
            )}
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto', padding: '6px' }}>
          {messages.map(renderMessage)}
          <div ref={endRef} />
        </div>

        {isSelfService && awaitingFeedback && (
          <Paper style={{ padding: '6px', margin: '3px', position: 'sticky', bottom: 0 }}>
            <Typography style={{ ...fontSize, marginBottom: '6px' }}>Помогна ли?</Typography>
            <div style={{ display: 'flex', gap: '6px' }}>
              <Button
                variant="contained"
                disabled={stepping}
                onClick={onResolved}
                style={{ flex: 1 }}
              >
                <Typography component="span" style={fontSize}>
                  Да
                </Typography>
              </Button>
              <Button
                variant="contained"
                color="secondary"
                disabled={stepping}
                onClick={onEscalate}
                style={{ flex: 1 }}
              >
                <Typography component="span" style={fontSize}>
                  Не, към координатор
                </Typography>
              </Button>
            </div>
          </Paper>
        )}

        {((isSelfService && !awaitingFeedback && !describeMode) || (newChat && !generalTarget)) && (
          <Paper style={{ padding: '6px', margin: '3px', position: 'sticky', bottom: 0 }}>
            <Typography style={{ ...fontSize, marginBottom: '6px' }}>
              {browsedGroup ? browsedGroup.title : 'За какво е въпросът?'}
            </Typography>
            <TextField
              fullWidth
              placeholder="Търси тема"
              value={topicSearch}
              onChange={x => setTopicSearch(x.target.value)}
              inputProps={{ style: fontSize }}
              style={{ marginBottom: '6px' }}
            />
            {(searchResults || topicOptions).map(x => (
              <Button
                key={x.id}
                fullWidth
                variant="contained"
                disabled={stepping}
                onClick={() => onSelectCategory(x)}
                style={{ margin: '3px 0', justifyContent: 'flex-start' }}
              >
                <Typography component="span" style={fontSize}>
                  {searchResults ? x.path : x.title}
                </Typography>
              </Button>
            ))}
            {searchResults && !searchResults.length && (
              <Typography style={fontSize}>Няма такава тема</Typography>
            )}
            {parentId && !searchResults && (
              <Button
                fullWidth
                disabled={stepping}
                onClick={() => setBrowseParentId(null)}
                style={{ margin: '3px 0' }}
              >
                <Typography component="span" style={fontSize}>
                  ← Всички теми
                </Typography>
              </Button>
            )}
          </Paper>
        )}

        {showInput && (
          <Paper style={{ margin: '3px', position: 'sticky', bottom: 0 }}>
            {generalTopics && (
              <div style={{ padding: '6px 6px 0' }}>
                <Typography style={fontSize}>
                  {browsedGroup ? browsedGroup.title : 'Изберете тема или напишете съобщение'}
                </Typography>
                {topicOptions.map(x => (
                  <Button
                    key={x.id}
                    fullWidth
                    variant="contained"
                    disabled={stepping}
                    onClick={() => onSelectCategory(x)}
                    style={{ margin: '3px 0', justifyContent: 'flex-start' }}
                  >
                    <Typography component="span" style={fontSize}>
                      {x.title}
                    </Typography>
                  </Button>
                ))}
                {parentId && (
                  <Button
                    fullWidth
                    disabled={stepping}
                    onClick={() => setBrowseParentId(null)}
                    style={{ margin: '3px 0' }}
                  >
                    <Typography component="span" style={fontSize}>
                      ← Всички теми
                    </Typography>
                  </Button>
                )}
              </div>
            )}
            {(isGeneralChat || (newChat && generalTarget)) && quickReplies.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '6px 6px 0' }}>
                {quickReplies.map(reply => (
                  <Button
                    key={reply}
                    size="small"
                    variant="outlined"
                    onClick={() => send(newClientMessageId(), reply)}
                  >
                    <Typography component="span" style={fontSize}>
                      {reply}
                    </Typography>
                  </Button>
                ))}
              </div>
            )}
            <DraftAttachments
              drafts={drafts}
              onRemove={draft => setDrafts(current => current.filter(x => x !== draft))}
            />
            <div style={{ display: 'flex', alignItems: 'flex-end', padding: '6px' }}>
              {attachmentsConfig && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={onPickPhotos}
                  />
                  <IconButton
                    onClick={() => fileInputRef.current.click()}
                    disabled={!photosAllowed || drafts.length >= attachmentsConfig.maxPerMessage}
                    style={{ marginRight: '6px' }}
                  >
                    <PhotoCamera />
                  </IconButton>
                </>
              )}
              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder={describeMode && !isGeneralChat ? 'Опишете проблема' : 'Съобщение'}
                value={text}
                onChange={x => setText(x.target.value)}
                inputProps={{ maxLength: 4000, style: fontSize }}
              />
              <Button
                variant="contained"
                color="secondary"
                onClick={onSend}
                disabled={!text.trim() && !drafts.length}
                style={{ marginLeft: '6px' }}
              >
                <Send />
              </Button>
            </div>
          </Paper>
        )}
        <Dialog open={!!confirmTopic} onClose={() => setConfirmTopic(null)}>
          {confirmTopic && (
            <>
              <DialogTitle>Потвърждение</DialogTitle>
              <DialogContent>
                <Typography style={fontSize}>
                  {`„${confirmTopic.title}“ прекъсва услугата на клиента за няколко минути. Да го изпратя ли?`}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setConfirmTopic(null)}>
                  <Typography component="span" style={fontSize}>
                    Не
                  </Typography>
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    const topic = confirmTopic;
                    setConfirmTopic(null);
                    runTopic(topic, true);
                  }}
                >
                  <Typography component="span" style={fontSize}>
                    Да, изпрати
                  </Typography>
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Dialog>
    )
  );
};

export default withChatBoundary(ChatDialog);
